import {
    getAuth,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    signOut,
    onAuthStateChanged,
    updateProfile,
    updatePassword as fbUpdatePassword,
    sendPasswordResetEmail as fbSendPasswordResetEmail,
    GoogleAuthProvider,
    connectAuthEmulator,
    Auth,
    User,
} from 'firebase/auth'
import {
    firebaseApp,
    useFirebaseEmulator,
    emulatorHost,
} from './init-firebase'
import { database } from './database'
import store from '../../store'
import {
    SET_BUSY,
    SET_ERROR,
    SET_USER,
} from '../../reducers/auth'
import {
    EVENT_REGISTRATION,
    sendWebViewEvent,
} from './events-webview'
import { generateUUID } from './uuid-generator'

let authInstance: Auth | null = null

/**
 * Lazy Auth singleton
 */
export const auth = (): Auth => {
    if (!authInstance) {
        authInstance = getAuth(firebaseApp)
        if (useFirebaseEmulator()) {
            connectAuthEmulator(authInstance, `http://${emulatorHost()}:9099`, { disableWarnings: true })
        }
    }
    return authInstance
}

/**
 * Auth providers
 */
export const providers = (): { googleProvider: GoogleAuthProvider } => ({
    googleProvider: new GoogleAuthProvider(),
})

/**
 * Subscribe to auth state changes.
 *
 * @param {Function} callback
 * @return {Function} unsubscribe
 */
export const onAuthStateChange = (callback: (user: User | null) => void): (() => void) =>
    onAuthStateChanged(auth(), callback)

/**
 * Update password of given user.
 *
 * @param {User} user
 * @param {string} newPassword
 * @return {Promise<void>}
 */
export const updateUserPassword = (user: User, newPassword: string): Promise<void> =>
    fbUpdatePassword(user, newPassword)

/**
 * Send password reset email.
 *
 * @param {string} email
 * @return {Promise<void>}
 */
export const sendPasswordResetEmail = (email: string): Promise<void> =>
    fbSendPasswordResetEmail(auth(), email)

export interface UserData {
    name: string
    surname: string
    company?: string
    isSuperAdmin?: boolean
}

/**
 * Update user data
 *
 * @param {User} user
 * @param {UserData} userData
 */
export const updateUserData = (user: User, userData: UserData): void => {
    const {
        name,
        surname,
        company,
        isSuperAdmin,
    } = userData

    updateProfile(user, {
        displayName: `${name} ${surname}`,
    })

    database('user')
        .doc(user.uid)
        .set({
            uid: user.uid,
            name: name,
            surname: surname,
            approved: false,
            defaultPasswordChanged: false,
            company: company || '',
            superAdmin: isSuperAdmin || false,
        })
}

/**
 * Register user
 *
 * @param {string} email
 * @param {string} password
 * @param {UserData} userData
 * @return {Promise<void>}
 */
export const register = (email: string, password: string, userData: UserData): Promise<void> => (
    createUserWithEmailAndPassword(auth(), email, password)
        .then(() => {
            const currentUser = auth().currentUser
            if (currentUser) {
                updateUserData(currentUser, userData)
            }
        })
        .then(() => {
            const eventData = {
                close: true,
                data: {
                    user: auth().currentUser,
                    credentials: {
                        email,
                        password,
                    },
                },
            }

            sendWebViewEvent(EVENT_REGISTRATION, eventData)
        })
        .catch(err => console.error('err', err))
)

/**
 * Login
 *
 * @param {string} email
 * @param {string} password
 * @return {Promise<User | null>}
 */
export const login = (email: string, password: string): Promise<User | null> => {
    if (!email || !password || email === '' || password === '') {
        store.dispatch({
            type: SET_ERROR,
            error: 'auth/fill-all',
        })

        throw new Error()
    }

    store.dispatch({
        type: SET_BUSY,
        busy: true,
    })

    return signInWithEmailAndPassword(auth(), email, password)
        .catch(err => {
            store.dispatch({
                type: SET_ERROR,
                error: err.code,
            })

            throw new Error(err.code)
        })
        .then(() => {
            const currentUser = auth().currentUser

            if (currentUser) {
                database('user')
                    .doc(currentUser.uid)
                    .update({
                        userToken: generateUUID(),
                    })
            }

            return currentUser
        })
        .finally(() => store.dispatch({
            type: SET_BUSY,
            busy: false,
        }))
}

/**
 * Logout
 *
 * @return {Promise<void>}
 */
export const logout = (): Promise<void> => (
    signOut(auth())
        .then(() => store.dispatch({
            type: SET_USER,
            user: null,
        }))
        .then(() => undefined)
)

/**
 * Fetch logged user and save it into storage
 *
 * @return {Promise<unknown>}
 */
export const fetchLoggedUser = (): Promise<unknown> => {
    store.dispatch({
        type: SET_BUSY,
        busy: true,
    })

    const user = auth().currentUser

    if (!user) {
        store.dispatch({
            type: SET_BUSY,
            busy: false,
        })
        return Promise.resolve()
    }

    return database('user')
        .doc(user.uid)
        .get()
        .then(doc => {
            if (!doc.exists) {
                return Promise.reject(404)
            }

            store.dispatch({
                type: SET_USER,
                user: {
                    ...doc.data(),
                    uid: user.uid,
                    displayName: user.displayName,
                },
            })

            return doc.data()
        })
        .catch(err => (err !== 404 ? Promise.reject(500) : Promise.reject(err)))
        .finally(() => {
            store.dispatch({
                type: SET_BUSY,
                busy: false,
            })
        })
}
